// src/sync/sync.js
// Keeping the data on this device and the data on the server the same, in both directions.
//
// The device remembers, per user, what it last agreed on with the server: the server's revision (`serverRev`) and the
// hash of its own data (`syncedHash`). Since then the device has changed if its hash differs, and the server has
// changed if its revision does. Only one side changed: the other one catches up. Both changed (and differ): that is a
// conflict, and the user chooses which version to keep; the other one is saved as a backup first (by the backend).
import { localFetch, requestVia, serverFetch } from '../api';

export const serverClient = (userId) => (method, path, body) => requestVia(serverFetch, method, path, userId, body);
export const localClient = (userId) => (method, path, body) => requestVia(localFetch, method, path, userId, body);

// What to do, given what the device remembers and what both sides look like now:
// 'idle' | 'pull' | 'push' | 'record' (the same data on both sides: just remember it) | 'conflict'
export function decide(meta, local, server) {
    if (local.hash === server.hash && local.empty === server.empty) return meta && meta.serverRev === server.rev && meta.syncedHash === local.hash ? 'idle' : 'record';
    if (!meta) {
        if (local.empty) return 'pull';
        if (server.empty) return 'push';
        return 'conflict';
    }
    const deviceChanged = local.hash !== meta.syncedHash;
    const serverChanged = server.rev !== meta.serverRev;
    if (deviceChanged && serverChanged) return 'conflict';
    if (deviceChanged) return 'push';
    if (serverChanged) return 'pull';
    return 'idle';
}

// What the device keeps after agreeing with the server: the revision and hash (to tell who changed since), and the
// data itself (the common ancestor, for merging when both changed).
const rememberer = (userId, local) => async (serverRev, syncedHash, data) => {
    if (data) await local('PUT', '/localSync/base', { data });
    await local('PUT', '/localSync/meta', { serverRev, syncedHash });
};

async function pull({ userId, server, local }) {
    const remote = await server('GET', '/sync');
    await local('PUT', '/sync', { baseRev: 0, force: true, data: remote.data });
    const now = await local('GET', '/sync/state');
    await rememberer(userId, local)(remote.rev, now.hash, remote.data);
}

async function push({ userId, server, local }, baseRev, force) {
    const mine = await local('GET', '/sync');
    const answer = await server('PUT', '/sync', { baseRev, force: Boolean(force), data: mine.data });
    await rememberer(userId, local)(answer.rev, mine.hash, mine.data);
}

// One round of syncing. Resolves with { outcome: 'idle' | 'pulled' | 'pushed' | 'conflict' | 'offline' | 'error', message? }.
export async function runSync(userId, clients = {}) {
    const context = { userId, server: clients.server || serverClient(userId), local: clients.local || localClient(userId) };
    try {
        const [meta, mine, theirs] = await Promise.all([
            context.local('GET', '/localSync/meta'),
            context.local('GET', '/sync/state'),
            context.server('GET', '/sync/state'),
        ]);
        const action = decide(meta, mine, theirs);
        if (action === 'idle') {
            // in step: make sure the agreed version is kept too (devices from before merging have none)
            if ((await context.local('GET', '/localSync/base')) === null) {
                const now = await context.local('GET', '/sync');
                await context.local('PUT', '/localSync/base', { data: now.data });
            }
            return { outcome: 'idle' };
        }
        if (action === 'record') {
            const now = await context.local('GET', '/sync');
            await rememberer(userId, context.local)(theirs.rev, mine.hash, now.data);
            return { outcome: 'idle' };
        }
        if (action === 'conflict') return await merge(context, {});
        if (action === 'pull') {
            await pull(context);
            return { outcome: 'pulled' };
        }
        try {
            await push(context, theirs.rev);
        } catch (error) {
            if (error.status === 409) return await merge(context, {});
            throw error;
        }
        return { outcome: 'pushed' };
    } catch (error) {
        return failure(error);
    }
}

// Both sides changed: merge them on top of the version they last agreed on (see backend/sync_merge.py). What only one
// side changed is taken; what both changed differently comes back as `conflicts`, to be answered one by one (`choices`
// maps a conflict's key to 'mine' or 'theirs'). Without a common version (or with a problem) it is a plain conflict
// between the two whole versions.
async function merge(context, choices) {
    const remote = await context.server('GET', '/sync');
    const answer = await context.local('POST', '/localSync/merge', { theirs: remote.data, choices });
    if (answer.noBase) return { outcome: 'conflict' };
    const open = answer.conflicts.filter((conflict) => !(conflict.key in choices));
    if (open.length > 0) return { outcome: 'conflict', conflicts: open };
    await context.local('PUT', '/sync', { baseRev: 0, force: true, data: answer.merged });
    try {
        await push(context, remote.rev);
    } catch (error) {
        if (error.status === 409) return { outcome: 'conflict' }; // the server moved on meanwhile: start again
        throw error;
    }
    return { outcome: 'merged' };
}

// The answers to the questions of a merge.
export async function resolveMerge(userId, choices, clients = {}) {
    const context = { userId, server: clients.server || serverClient(userId), local: clients.local || localClient(userId) };
    try {
        return await merge(context, choices);
    } catch (error) {
        return failure(error);
    }
}

// The user's choice after a conflict: 'device' (this device's version wins) or 'server' (the server's does).
export async function resolveConflict(userId, choice, clients = {}) {
    const context = { userId, server: clients.server || serverClient(userId), local: clients.local || localClient(userId) };
    try {
        if (choice === 'server') {
            await pull(context);
            return { outcome: 'pulled' };
        }
        const theirs = await context.server('GET', '/sync/state');
        await push(context, theirs.rev, true);
        return { outcome: 'pushed' };
    } catch (error) {
        return failure(error);
    }
}

// A failure without an HTTP status never reached the server: there is no connection.
function failure(error) {
    if (error.status === undefined) return { outcome: 'offline' };
    return { outcome: 'error', message: error.message };
}
